/**
 * Pure RBAC access types + capability logic (RBAC Phase 4).
 *
 * No React / no config imports, so it's unit-testable in isolation and safe to
 * import from anywhere. The `useAccess` hook (useAccess.ts) layers React Query
 * fetching on top of these.
 */

/** Capability string constants — mirror app/core/rbac.py Capability values. */
export const Capability = {
  MonitoringView: 'monitoring.view',
  MonitoringContribute: 'monitoring.contribute',
  MonitoringShare: 'monitoring.share',
  MonitoringManageCollection: 'monitoring.manage_collection',
  TeamManage: 'team.manage',
  FirmManage: 'firm.manage',
  FirmAuditView: 'firm.audit_view',
  PlatformManage: 'platform.manage',
  // GO Lab (internal domain). GolabAnnotate is the base of the LAB_* ladder and
  // is held by every internal role incl. PLATFORM_ADMIN, so it doubles as the
  // "can reach GO Lab" gate.
  GolabAnnotate: 'golab.annotate',
  GolabAdjudicate: 'golab.adjudicate',
  GolabRunEval: 'golab.run_eval',
  GolabManageDatasets: 'golab.manage_datasets',
  GolabManageIntel: 'golab.manage_intel',
  GolabDeploy: 'golab.deploy',
  GolabAuditView: 'golab.audit_view',
} as const;

export type CapabilityValue = (typeof Capability)[keyof typeof Capability];

export interface TeamRef {
  id: string;
  name: string;
}

export interface AccessMe {
  userId: string;
  organizationId: string;
  capabilities: string[];
  teamIds: string[];
  /** The caller's own teams with names — populates scope pickers (e.g. the
   *  owning-team dropdown at collection creation). May be absent on older
   *  API responses; callers should fall back to `teamIds`. */
  teams?: TeamRef[];
  enabledFeatures: string[];
}

/**
 * Pure capability check. `undefined` access ⇒ deny (fail-closed while loading,
 * matching the backend default).
 */
export function canWith(
  access: AccessMe | undefined,
  capability: CapabilityValue,
): boolean {
  return !!access && access.capabilities.includes(capability);
}

export function hasFeatureWith(
  access: AccessMe | undefined,
  feature: string,
): boolean {
  return !!access && access.enabledFeatures.includes(feature);
}
